import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Chip, Box } from '@mui/material';
import GenericDialog from 'src/components/GenericDialog';
import DynamicFormWidget from 'src/components/dynamic-widget/DynamicFormWidget';
import { DynamicField, DynamicFieldTypes } from 'src/components/dynamic-widget';
import RepeatingPropertiesField from 'src/components/dynamic-widget/RepeatingPropertiesField';
import { DataService } from 'src/services/data-service';
import { LovService, LovDictionary } from 'src/services/lov-service';
import { StorageUnit, StorageUnitDetail } from 'src/types/StorageUnit.types';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { any } from 'prop-types';
import Loader from 'src/components/Loader';

interface Warehouse {
  Id: string;
  Name: string;
}

interface CreateStorageUnitDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  storageUnits: StorageUnitDetail[];
  defaultWarehouseId?: string;
  defaultParentStorageUnitId?: string;
  infoWarehouse?: { Code: string; Name: string };
  infoParentStorageUnit?: { Code: string; Name: string };
  forceParentWarehouseId?: string;
  forceParentStorageUnitId?: string;
}

const CreateStorageUnitDrawer: React.FC<CreateStorageUnitDrawerProps> = ({
  open,
  onClose,
  onSuccess,
  storageUnits,
  defaultWarehouseId,
  defaultParentStorageUnitId,
  infoWarehouse,
  infoParentStorageUnit,
  forceParentWarehouseId,
  forceParentStorageUnitId,
}) => {
  const [formValues, setFormValues] = useState({
    Code: '',
    Name: '',
    ParentWarehouseId: forceParentWarehouseId || defaultWarehouseId || '',
    ParentStorageUnitId: forceParentStorageUnitId || defaultParentStorageUnitId || '',
    StorageTypeId: '',
    Properties: [] as Array<{ key: string; value: string }>,
  });

  const dispatch = useAppDispatch();
  const [warehouseOptions, setWarehouseOptions] = useState<LovDictionary>({});
  const [predefinedValueOptions, setPredefinedValueOptions] = useState<LovDictionary>({});
  const [storageTypeOptions, setStorageTypeOptions] = useState<LovDictionary>({});
  const [warehouseStorageUnits, setWarehouseStorageUnits] = useState<LovDictionary>({});
  const [loadingLovs, setLoadingLovs] = useState(false);
  const [loadingWarehouseUnits, setLoadingWarehouseUnits] = useState(false);

  useEffect(() => {
    setFormValues({
      Code: '',
      Name: '',
      ParentWarehouseId: forceParentWarehouseId || defaultWarehouseId || '',
      ParentStorageUnitId: forceParentStorageUnitId || defaultParentStorageUnitId || '',
      StorageTypeId: '',
      Properties: [],
    });
  }, [defaultWarehouseId, defaultParentStorageUnitId, forceParentWarehouseId, forceParentStorageUnitId]);

  useEffect(() => {
    if (open) {
      loadAllLovOptions();
    }
  }, [open]);

  // Load warehouse-specific storage units when warehouse changes
  useEffect(() => {
    const warehouseId = forceParentWarehouseId || formValues.ParentWarehouseId;
    if (warehouseId && !forceParentStorageUnitId) {
      loadWarehouseStorageUnits(warehouseId);
    }
  }, [formValues.ParentWarehouseId, forceParentWarehouseId, forceParentStorageUnitId]);

  const loadAllLovOptions = async () => {
    setLoadingLovs(true);
    try {
      const [warehouses, predefinedValues, storageTypes] = await Promise.allSettled([
        LovService.getLovDictionary('all-warehouses'),
        LovService.getLovDictionary('all-preDefinedValues'),
        LovService.getLovDictionary('all-storage-types'),
      ]);

      setWarehouseOptions(warehouses.status === 'fulfilled' ? warehouses.value : {});
      setPredefinedValueOptions(predefinedValues.status === 'fulfilled' ? predefinedValues.value : {});
      setStorageTypeOptions(storageTypes.status === 'fulfilled' ? storageTypes.value : {});
    } catch (error) {
      console.error('Failed to load LOV options:', error);
      dispatch(showAlert({ message: 'Failed to load form options', type: 'error' }));
    } finally {
      setLoadingLovs(false);
    }
  };

  const loadWarehouseStorageUnits = async (warehouseId: string) => {
    setLoadingWarehouseUnits(true);
    try {
      const warehouseStorageUnitsData = await LovService.getLovDictionary(`all-storage-units/warehouse/${warehouseId}`);
      setWarehouseStorageUnits(warehouseStorageUnitsData);
    } catch (error) {
      console.error('Failed to load warehouse storage units:', error);
      setWarehouseStorageUnits({});
    } finally {
      setLoadingWarehouseUnits(false);
    }
  };

  const filteredParentStorageUnits = useMemo(() => {
    return storageUnits.filter(
      su => su.warehouseId === (forceParentWarehouseId || formValues.ParentWarehouseId)
    );
  }, [storageUnits, formValues.ParentWarehouseId, forceParentWarehouseId]);

  const storageUnitOptions = useMemo(() => {
    if (Object.keys(warehouseStorageUnits).length > 0) {
      return warehouseStorageUnits;
    }
    
    return filteredParentStorageUnits.reduce((acc, storageUnit) => {
      if (storageUnit.id) {
        acc[storageUnit.id] = `${storageUnit.code} - ${storageUnit.name}`;
      }
      return acc;
    }, {} as Record<string, string>);
  }, [filteredParentStorageUnits, warehouseStorageUnits]);

  const handleFieldChange = useCallback((field: string, value: any) => {
    setFormValues(prev => ({
      ...prev,
      [field]: field === 'Code' ? (typeof value === 'string' ? value.toUpperCase() : value) : value,
      ...(field === 'ParentWarehouseId' ? { ParentStorageUnitId: '' } : {}),
    }));
  }, []);

  const formConfig: Record<string, DynamicFieldTypes> = useMemo(() => {
    const config: Record<string, DynamicFieldTypes> = {
      Code: {
        type: DynamicField.TEXT,
        required: true,
        name: 'Code',
        title: 'Code',
        value: formValues.Code,
        disabled: loadingLovs,
        onChange: (val: string) => handleFieldChange('Code', val),
        helperText: null,
      },
      Name: {
        type: DynamicField.TEXT,
        required: true,
        name: 'Name',
        title: 'Name',
        disabled: loadingLovs,
        value: formValues.Name,
        onChange: (val: string) => handleFieldChange('Name', val),
      },
      StorageTypeId: {
        type: DynamicField.SELECT,
        required: true,
        name: 'StorageTypeId',
        title: 'Storage Type',
        value: formValues.StorageTypeId,
        items: storageTypeOptions,
        onChange: (val: string) => handleFieldChange('StorageTypeId', val),
        disabled: loadingLovs,
      },
    };

    if (!infoWarehouse && !forceParentWarehouseId) {
      config.ParentWarehouseId = {
        type: DynamicField.SELECT,
        required: true,
        name: 'ParentWarehouseId',
        title: 'Parent Warehouse',
        value: formValues.ParentWarehouseId,
        items: warehouseOptions,
        onChange: (val: string) => handleFieldChange('ParentWarehouseId', val),
        disabled: loadingLovs,
      };
    }

    if (!infoParentStorageUnit && !forceParentStorageUnitId) {
      config.ParentStorageUnitId = {
        type: DynamicField.SELECT,
        required: false,
        name: 'ParentStorageUnitId',
        title: 'Parent Storage Unit',
        value: formValues.ParentStorageUnitId,
        items: { '': 'None', ...storageUnitOptions },
        onChange: (val: string) => handleFieldChange('ParentStorageUnitId', val),
        disabled: loadingLovs || loadingWarehouseUnits,
      };
    }


    return config;
  }, [
    formValues,
    infoWarehouse,
    infoParentStorageUnit,
    forceParentWarehouseId,
    forceParentStorageUnitId,
    warehouseOptions,
    storageUnitOptions,
    handleFieldChange,
    loadingLovs,
    loadingWarehouseUnits,
  ]);

  const formLayout = useMemo(() => {
    const groups = [
      {
        title: 'Basic Info',
        fields: ['Code', 'Name', 'StorageTypeId'],
        columns: 3 as any,
      }
    ];

    const parentFields = [];
    if (!infoWarehouse && !forceParentWarehouseId) parentFields.push('ParentWarehouseId');
    if (!infoParentStorageUnit && !forceParentStorageUnitId) parentFields.push('ParentStorageUnitId');
    
    if (parentFields.length > 0) {
      groups.push({
        title: 'Parent Assignment',
        fields: parentFields,
        columns: 2 as 2
      });
    }

    return { groups };
  }, [infoWarehouse, forceParentWarehouseId, infoParentStorageUnit, forceParentStorageUnitId]);

  const handleSubmit = useCallback(async (data: Record<string, any>): Promise<boolean> => {
    try {
      const submitData: any = {
        code: formValues.Code.toUpperCase(),
        name: formValues.Name,
        parentWarehouseId: forceParentWarehouseId || formValues.ParentWarehouseId || data.ParentWarehouseId,
        storageTypeId: formValues.StorageTypeId,
        properties: {}
      };

      const finalParentStorageUnitId = forceParentStorageUnitId !== undefined 
        ? forceParentStorageUnitId 
        : (formValues.ParentStorageUnitId || data.ParentStorageUnitId);
      
      if (finalParentStorageUnitId) {
        submitData.parentStorageUnitId = finalParentStorageUnitId;
      }

      const properties = data.Properties || formValues.Properties || [];
      properties.forEach((property: { key: string; value: string }) => {
        if (property.key && property.value) {
          const predefinedValueName = predefinedValueOptions[property.key] || property.key;
          submitData.properties[predefinedValueName] = property.value;
        }
      });

      const endpoint = 'api/StorageUnit';
      const successMessage = 'Storage Unit added!';
      const response = await DataService.post(endpoint, submitData);
      const result = await response.json();
      
      if (response.ok && result.success) {
        onSuccess();
        dispatch(showAlert({ message: successMessage, type: 'success' }));
        return true;
      } else {
        dispatch(showAlert({ message: result.Message || 'An error occurred!', type: 'error' }));
        return false;
      }
    } catch (error) {
      dispatch(showAlert({ message: 'An unexpected error occurred!', type: 'error' }));
      return false;
    }
  }, [formValues, predefinedValueOptions, forceParentWarehouseId, forceParentStorageUnitId, onSuccess, dispatch]);

        if(loadingLovs){
        return (
          <GenericDialog title="Add Shipment" open={open} onClose={onClose}>
            <Loader 
              variant="section"
              type="warehouse"
              size="medium"
              message="Loading Options..."
              subMessage="Please wait while we process your request"
              showProgress={true}
            />
          </GenericDialog>
        );
      }

  return (
    <GenericDialog 
      title={'Add Storage Unit'} 
      open={open} 
      onClose={onClose}
    >
      
      {(infoWarehouse || infoParentStorageUnit) && (
        <Box 
          mb={3} 
          p={2} 
          sx={{ 
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 1
          }}
        >
          <Box display="flex" flexDirection="column" gap={1}>
            {infoWarehouse && (
              <Box display="flex" alignItems="center" gap={1}>
                <Box 
                  component="span" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.secondary',
                    minWidth: 120
                  }}
                >
                  Warehouse:
                </Box>
                <Chip 
                  label={`${infoWarehouse.Code} - ${infoWarehouse.Name}`}
                  color="primary" 
                  variant="outlined" 
                  size="small"
                  sx={{ fontWeight: 500 }} 
                />
              </Box>
            )}
            {infoParentStorageUnit && (
              <Box display="flex" alignItems="center" gap={1}>
                <Box 
                  component="span" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.secondary',
                    minWidth: 120
                  }}
                >
                  Parent Storage Unit:
                </Box>
                <Chip 
                  label={`${infoParentStorageUnit.Code} - ${infoParentStorageUnit.Name}`}
                  color="secondary" 
                  variant="outlined" 
                  size="small"
                  sx={{ fontWeight: 500 }} 
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      <DynamicFormWidget
        title=""
        drawerMode
        fields={formConfig}
        onSubmit={handleSubmit}
        initialValues={formValues}
        layout={formLayout}
      >
        <RepeatingPropertiesField
          value={formValues.Properties}
          onChange={(properties) => handleFieldChange('Properties', properties)}
          predefinedValueOptions={predefinedValueOptions}
          disabled={loadingLovs}
          title="Properties"
        />
      </DynamicFormWidget>
    </GenericDialog>
  );
};

export default CreateStorageUnitDrawer;
